import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { LocalOffer, ContentCopy, CheckCircle, Block, HourglassEmpty } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import promoService from "../../services/promoService";
import PageHeader from "../../components/common/PageHeader";

const TEAL = "#00a6b2";
const MUTED = "#8a94a6";

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "";

const CustomerPromos = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await promoService.getMine();
        if (active) setPromos(data?.data?.promos || []);
      } catch (err) {
        if (active) enqueueSnackbar(err.response?.data?.message || "Failed to load promos", { variant: "error" });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [enqueueSnackbar]);

  const handleCopy = async (promo) => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopiedCode(promo.code);
      enqueueSnackbar("Promo code copied", { variant: "info" });
      setTimeout(() => setCopiedCode(""), 2000);
    } catch {
      enqueueSnackbar("Couldn't copy — copy it manually", { variant: "error" });
    }
  };

  return (
    <Box>
      <PageHeader
        title="Promos"
        subtitle="Codes you can redeem when creating a shipment, and what you've already used."
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      ) : promos.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
          <LocalOffer sx={{ fontSize: 40, color: MUTED, mb: 1 }} />
          <Typography sx={{ color: MUTED }}>No promos available right now — check back later.</Typography>
        </Card>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {promos.map((promo) => {
            const isAvailable = promo.status === "available";
            const isUsed = promo.status === "used";
            const isPending = promo.status === "pending";
            const chipLabel = isAvailable ? "Active" : isPending ? "Pending" : promo.statusMessage;
            const chipColor = isAvailable ? TEAL : isPending ? "#b8860b" : MUTED;
            const chipBg = isAvailable ? "rgba(0,166,178,0.12)" : isPending ? "rgba(184,134,11,0.12)" : "rgba(0,0,0,0.06)";
            return (
              <Card
                key={promo._id}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: isAvailable ? "rgba(0,166,178,0.35)" : isPending ? "rgba(184,134,11,0.35)" : "rgba(0,0,0,0.08)",
                  opacity: isAvailable || isPending ? 1 : 0.7,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontWeight: 700, color: "text.primary" }}>{promo.name}</Typography>
                      <Chip
                        size="small"
                        icon={
                          isUsed ? (
                            <CheckCircle sx={{ fontSize: 16 }} />
                          ) : isPending ? (
                            <HourglassEmpty sx={{ fontSize: 16 }} />
                          ) : !isAvailable ? (
                            <Block sx={{ fontSize: 16 }} />
                          ) : undefined
                        }
                        label={chipLabel}
                        sx={{ fontWeight: 600, bgcolor: chipBg, color: chipColor }}
                      />
                    </Box>
                    {promo.description && (
                      <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                        {promo.description}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: "text.primary", mt: 1, fontWeight: 600 }}>
                      ${promo.discountAmount.toFixed(2)} off
                      {promo.sizeGate ? ` shipments ${promo.sizeGate}` : ""}
                    </Typography>
                    <Typography variant="caption" sx={{ color: MUTED, display: "block", mt: 0.5 }}>
                      Valid {formatDate(promo.startDate)} – {formatDate(promo.endDate)}
                      {isUsed && promo.redeemedAt ? ` · Redeemed ${formatDate(promo.redeemedAt)}` : ""}
                    </Typography>
                    {isPending && (
                      <Typography variant="caption" sx={{ color: "#b8860b", display: "block", mt: 0.5, fontWeight: 600 }}>
                        {promo.statusMessage}
                      </Typography>
                    )}
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: isAvailable ? TEAL : "rgba(0,0,0,0.2)",
                        fontFamily: "monospace",
                        fontWeight: 700,
                        letterSpacing: 1,
                        color: isAvailable ? "text.primary" : MUTED,
                      }}
                    >
                      {promo.code}
                    </Box>
                    <Button
                      variant={isAvailable ? "contained" : "outlined"}
                      size="small"
                      disabled={!isAvailable}
                      startIcon={copiedCode === promo.code ? <CheckCircle /> : <ContentCopy />}
                      onClick={() => handleCopy(promo)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        // Explicit white text — without it, a contained
                        // Button's text color defaults to
                        // primary.contrastText, which in dark mode is a
                        // near-black chosen for the brighter dark-mode blue,
                        // not for this hardcoded teal override.
                        ...(isAvailable && { bgcolor: TEAL, color: "#fff", "&:hover": { bgcolor: "#008a94" } }),
                      }}
                    >
                      {!isAvailable ? (isPending ? "Pending" : promo.statusMessage) : copiedCode === promo.code ? "Copied" : "Copy Code"}
                    </Button>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CustomerPromos;
